import React, { useContext, useMemo } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { createSxProps } from '@modernloop/shared/components';
import { TrashIcon } from '@modernloop/shared/icons';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';

import { OrgSetupPageContext, PageType } from './IntegrationPageContext';

const useSxProps = () => {
  const sxProps = useMemo(() => {
    return {
      name: {
        paddingLeft: (theme) => theme.spacing(1),
      },
      containerIntegration: {
        minHeight: '40px',
        width: '100%',
        gap: (theme) => theme.spacing(1.5),
      },
      logoImage: {
        height: 20,
        width: 20,
        borderRadius: 4,
        boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.borderState.default}`,
      },
      integration: {
        minWidth: 'fit-content',
        flexGrow: 1,
      },
    };
  }, []);

  return createSxProps(sxProps);
};

interface Props {
  dataTestId?: string;
  logo?: string;
  name: string;
  badge?: React.ReactNode;
  isLoading?: boolean;
  connectButton?: React.ReactNode;
  removeButton?: React.ReactNode;
  // Extra action(s) rendered alongside the built-in buttons (e.g. a feature-gated connect option).
  extraActions?: React.ReactNode;
  onConnect?: (event: React.MouseEvent<HTMLElement>) => void;
  onManageSetup?: (event: React.MouseEvent<HTMLElement>) => void;
  onRemove?: (event: React.MouseEvent<HTMLElement>) => void;
  onReconnect?: (event: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  disableManageSetup?: boolean;
}

const IntegrationCard = ({
  dataTestId,
  logo,
  name,
  badge,
  isLoading = false,
  disabled = false,
  disableManageSetup = false,
  onConnect,
  onManageSetup,
  onReconnect,
  onRemove,
  connectButton,
  removeButton,
  extraActions,
}: Props) => {
  const { t } = useTranslation();
  const sxProps = useSxProps();
  const orgSetupPageContext = useContext(OrgSetupPageContext);

  return (
    <Box sx={sxProps.containerIntegration} display="flex" alignItems="center" textAlign="center">
      <Box sx={sxProps.integration} display="flex" alignItems="center">
        {logo && <Box component="img" alt={name} sx={sxProps.logoImage} src={logo} />}
        <Typography sx={sxProps.name}>{name}</Typography>
        {badge}
      </Box>
      {!isLoading && (
        <>
          {onConnect && connectButton}
          {!connectButton && onConnect && (
            <Button data-testid={`${dataTestId}-connect-button`} disabled={disabled} onClick={onConnect}>
              {t('hiring-dashboard-settings-integrations:settings.integrations.connect')}
            </Button>
          )}
          {onManageSetup && orgSetupPageContext.pageType !== PageType.OrgSetup && (
            <Button
              data-testid={`${dataTestId}-manage-setup-button`}
              disabled={disableManageSetup}
              onClick={onManageSetup}
            >
              {t('hiring-dashboard-settings-integrations:settings.integrations.manageSetup')}
            </Button>
          )}
          {onReconnect && orgSetupPageContext.pageType !== PageType.OrgSetup && (
            <Button data-testid={`${dataTestId}-reconnect-button`} disabled={disabled} onClick={onReconnect}>
              {t('hiring-dashboard-settings-integrations:settings.integrations.reconnect')}
            </Button>
          )}
          {extraActions}
          {onRemove &&
            !removeButton &&
            (orgSetupPageContext.pageType !== PageType.OrgSetup ? (
              <Button
                data-testid={`${dataTestId}-remove-integration-button`}
                sx={{ color: (theme) => theme.palette.error.main }}
                onClick={onRemove}
                disabled={disabled}
              >
                {t('hiring-dashboard-settings-integrations:settings.integrations.removeIntegration')}
              </Button>
            ) : (
              <Tooltip title={t('hiring-dashboard-settings-integrations:settings.integrations.removeIntegration')}>
                <IconButton onClick={onRemove}>
                  <TrashIcon />
                </IconButton>
              </Tooltip>
            ))}
          {removeButton && <>{removeButton}</>}
        </>
      )}
    </Box>
  );
};

export default IntegrationCard;
